<template>
    <UModal v-model="isOpen" :ui="{ width: 'sm:max-w-2xl' }">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-lg font-semibold">Share Call with Guest</h3>
                    <UButton color="gray" variant="ghost" icon="i-heroicons-x-mark-20-solid" @click="isOpen = false" />
                </div>
            </template>

            <div class="space-y-4">
                <p class="text-sm text-gray-600">Choose how you want to share this call with the guest:</p>

                <div class="grid grid-cols-2 gap-4">
                    <div
                        @click="state.selectedMethod = 'whatsapp'"
                        :class="[
                            'border-2 rounded-lg p-6 cursor-pointer transition-all',
                            state.selectedMethod === 'whatsapp'
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                        ]"
                    >
                        <div class="flex flex-col items-center text-center space-y-3">
                            <UIcon
                                name="i-mdi-whatsapp"
                                :class="[
                                    'size-12',
                                    state.selectedMethod === 'whatsapp' ? 'text-green-600' : 'text-gray-400'
                                ]"
                            />
                            <div>
                                <h4 class="font-semibold text-base">WhatsApp</h4>
                                <p class="text-xs text-gray-500 mt-1">Send link directly via WhatsApp</p>
                            </div>
                        </div>
                    </div>

                    <div
                        @click="state.selectedMethod = 'link'"
                        :class="[
                            'border-2 rounded-lg p-6 cursor-pointer transition-all',
                            state.selectedMethod === 'link'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        ]"
                    >
                        <div class="flex flex-col items-center text-center space-y-3">
                            <UIcon
                                name="i-heroicons-link"
                                :class="[
                                    'size-12',
                                    state.selectedMethod === 'link' ? 'text-blue-600' : 'text-gray-400'
                                ]"
                            />
                            <div>
                                <h4 class="font-semibold text-base">Copy Link</h4>
                                <p class="text-xs text-gray-500 mt-1">Generate link to share manually</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="state.selectedMethod === 'whatsapp'" class="border border-gray-200 rounded-lg p-4 space-y-3">
                    <UFormGroup label="Phone Number" :error="errors.phone">
                        <InputPhone
                            v-model="state.phone"
                            size="md"
                            :disabled="state.loadingWhatsApp"
                            />
                        <template #hint>
                            <div class="space-y-1">
                                <span v-if="customerPhone" class="text-xs text-gray-500 block">
                                    Customer: {{ customerPhone }}
                                    <UButton
                                        variant="link"
                                        size="xs"
                                        @click="state.phone = normalizeStoredPhone(customerPhone)"
                                        class="ml-1"
                                    >
                                        Use
                                    </UButton>
                                </span>
                                <span v-if="alternatePhone" class="text-xs text-gray-500 block">
                                    Alternate: {{ alternatePhone }}
                                    <UButton
                                        variant="link"
                                        size="xs"
                                        @click="state.phone = normalizeStoredPhone(alternatePhone)"
                                        class="ml-1"
                                    >
                                        Use
                                    </UButton>
                                </span>
                            </div>
                        </template>
                    </UFormGroup>

                    <UButton
                        icon="i-mdi-whatsapp"
                        color="green"
                        block
                        @click="sendViaWhatsApp"
                        :loading="state.loadingWhatsApp"
                        :disabled="!state.phone"
                    >
                        Send via WhatsApp
                    </UButton>
                </div>

                <div v-if="state.selectedMethod === 'link'" class="border border-gray-200 rounded-lg p-4 space-y-3">
                    <p class="text-sm text-gray-600">
                        Generate a secure link that can be shared manually.
                    </p>

                    <UButton
                        icon="i-heroicons-link"
                        color="blue"
                        block
                        @click="generateAndCopyLink"
                        :loading="state.loadingLink"
                    >
                        Generate & Copy Link
                    </UButton>
                </div>
            </div>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import { isValidPhone, normalizeStoredPhone } from '~/utils/phone'
import type { Call } from '~/types/call'

const props = defineProps<{
    open: boolean
    call?: Call
}>()

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'shared', room_sid: string): void
}>()

const toast = useToast()

const isOpen = computed({
    get: () => props.open,
    set: (value) => emit('update:open', value)
})

const state = reactive({
    selectedMethod: 'whatsapp' as 'whatsapp' | 'link',
    phone: '',
    loadingWhatsApp: false,
    loadingLink: false,
})

const errors = reactive({
    phone: '',
})

const customerPhone = computed(() => {
    return props.call?.customer?.phone
})

const alternatePhone = computed(() => {
    return props.call?.customer?.metadata?.alternate_phone
})

watch(isOpen, (value) => {
    if (value) {
        state.selectedMethod = 'whatsapp'
        state.phone = normalizeStoredPhone(customerPhone.value)
        errors.phone = ''
    }
})

async function generateAndCopyLink() {
    if (!props.call?.id) return

    state.loadingLink = true
    errors.phone = ''

    try {
        const { link, room_sid } = await $fetch<{ link: string; room_sid: string; whatsapp_sent: boolean }>('/api/calls/share', {
            method: 'POST',
            body: {
                call_id: props.call.id,
                phone_number: null,
                send_whatsapp: false,
            }
        })

        await navigator.clipboard.writeText(link)

        toast.add({
            title: 'Link copied to clipboard',
            description: 'Guest call link has been copied to clipboard',
            icon: 'i-heroicons-check-circle',
        })

        emit('shared', room_sid)
        isOpen.value = false
    } catch (error: any) {
        toast.add({
            title: 'Failed to generate link',
            description: error.data?.message || 'An error occurred',
            color: 'red',
            icon: 'i-heroicons-x-circle',
        })
    } finally {
        state.loadingLink = false
    }
}

async function sendViaWhatsApp() {
    if (!props.call?.id) return

    if (!state.phone) {
        errors.phone = 'Phone number is required'
        return
    }

    if (!isValidPhone(state.phone, { mobileOnly: true })) {
        errors.phone = 'Enter a valid mobile number'
        return
    }

    state.loadingWhatsApp = true
    errors.phone = ''

    try {
        const { link, room_sid, whatsapp_sent } = await $fetch<{ link: string; room_sid: string; whatsapp_sent: boolean }>('/api/calls/share', {
            method: 'POST',
            body: {
                call_id: props.call.id,
                phone_number: state.phone,
                send_whatsapp: true,
            }
        })

        if (whatsapp_sent) {
            toast.add({
                title: 'WhatsApp sent',
                description: `Guest call link sent to ${state.phone}`,
                icon: 'i-heroicons-check-circle',
            })

            emit('shared', room_sid)
            isOpen.value = false
        } else {
            toast.add({
                title: 'Failed to send WhatsApp',
                description: 'Link generated but WhatsApp message failed to send',
                color: 'amber',
                icon: 'i-heroicons-exclamation-triangle',
            })
        }
    } catch (error: any) {
        toast.add({
            title: 'Failed to send',
            description: error.data?.message || 'An error occurred',
            color: 'red',
            icon: 'i-heroicons-x-circle',
        })
    } finally {
        state.loadingWhatsApp = false
    }
}
</script>
